package top.wcpe.allincore.dsl.ir.model.animation

import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyOrderedMap

/**
 * UI 动画关键帧 IR。
 *
 * 表示在归一化时间轴上的某个时间点，元素属性应当达到的目标值集合。
 * 多个关键帧按 [offset] 排序后，运行时在相邻关键帧之间做线性插值。
 *
 * @throws IllegalArgumentException offset 越界时抛出
 */
class UiKeyframe(
    // val 属性生成 getOffset()，与原 Java getter 名一致。
    /** 归一化时间偏移，取值范围 [0.0, 1.0]，0 表示动画起点，1 表示终点。 */
    val offset: Double,
    // 声明可空以复刻原 Java Objects.requireNonNull 的 NullPointerException + 中文消息（非 Kotlin 参数内建 NPE）。
    props: Map<String, UiBinding>?,
) {

    /** 该关键帧上需要达到的属性目标值集合，键为属性名，值为字面量绑定，保序、不可变。 */
    val props: Map<String, UiBinding>

    init {
        require(!(offset < 0.0 || offset > 1.0)) { "offset 必须在 0.0 到 1.0 之间" }
        val src = props ?: throw NullPointerException("props 不能为空")
        // 防御式保序拷贝，并校验键值非空（Java 侧可经原始类型塞入 null，故显式判空）。
        this.props = ReadOnlyOrderedMap(copyProps(src))
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiKeyframe) return false
        return offset.compareTo(other.offset) == 0 && props == other.props
    }

    // 逐位复刻 Objects.hash(offset, props)：种子 1、31 倍累乘、Double.hashCode / Map.hashCode。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + offset.hashCode()
        result = 31 * result + props.hashCode()
        return result
    }

    override fun toString(): String =
        "UiKeyframe{" + offset + ", props=" + props.keys + "}"

    companion object {
        // 防御式保序拷贝，键值为 null 时抛 NullPointerException（复刻原 Java Objects.requireNonNull）。
        @Suppress("SENSELESS_COMPARISON")
        private fun copyProps(props: Map<String, UiBinding>): LinkedHashMap<String, UiBinding> {
            val copied = LinkedHashMap<String, UiBinding>(props.size)
            for (entry in props.entries) {
                val key = entry.key
                val value = entry.value
                if (key == null) throw NullPointerException("prop key 不能为空")
                if (value == null) throw NullPointerException("prop value 不能为空")
                copied[key] = value
            }
            return copied
        }
    }
}
